import { useEffect, useState } from 'react';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from '../../RnRCommonComponent/CoreBadge/rewardsConstants';
import gql from 'graphql-tag';

const QUERY_BADGES_BY_COMPANY_ID = gql`
	query QueryBadgesByCompanyIdIndex(
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryBadgesByCompanyIdIndex(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				allowSelfNomination
				companyId
				badgeTypeId
				assignmentSSORequiredId
				recipientPayTypeId
				recipientSSORequiredId
				physicalAwardActivityId
				iconId
				snappyGrantId
				snappyGrant {
					id
					externalSnappyId
					snappyCampaignId
					name
					description
					costInCurrency
				}
				name
				label
				description
				image
				color
				gradientColor
				points
				bonusPoints
				rewardBonus
				rewardMaxBonus
				assignmentLimit
				assignmentLevelId
				recipientLevelId
				isPreApproved
				isRewardRequiresDate
				visibility
				isAssignmentSSORequired
				isRecipientSSORequired
				automationRule
				externalWorkFlowId
				isAssignmentRestricted
				shape
				badgeRotation
				customImageKey
				customImageSignedUrl
				labelColor
				customIconKey
				customIconSignedUrl
				category
				certificateConfig {
					id
					name
					key
				}
			}
			nextToken
		}
	}
`;

const CREATE_NOMINATION_CONFIG = gql`
	mutation CreateNominationConfiguration(
		$input: NominationConfigurationInput!
	) {
		createNominationConfiguration(input: $input) {
			id
			createdAt
			startDate
			status
			updatedAt
			badgeId
			companyId
			configId
			customFormId
			endDate
			awardedCount
			nominationConfigurationId
			nominatorEmployeeLevelId
			nominatorEmployeePayTypeId
			nominatorEmployeelogInTypeId
			nomineeEmployeeLevelId
			nomineeEmployeePayTypeId
			nomineeEmployeelogInTypeId
			snappyCampaignTierId
			snappyGrantId
		}
	}
`;

const UPDATE_NOMINATION_CONFIG = gql`
	mutation UpdateNominationConfiguration(
		$input: NominationConfigurationInput!
	) {
		updateNominationConfiguration(input: $input) {
			id
			createdAt
			startDate
			status
			updatedAt
			badgeId
			companyId
			configId
			customFormId
			endDate
			awardedCount
			nominationConfigurationId
			nominatorEmployeeLevelId
			nominatorEmployeePayTypeId
			nominatorEmployeelogInTypeId
			nomineeEmployeeLevelId
			nomineeEmployeePayTypeId
			nomineeEmployeelogInTypeId
			snappyCampaignTierId
			snappyGrantId
		}
	}
`;

const GET_NOMINATION_CONFIG = gql`
	query GetNominationConfiguration($companyId: ID, $configId: ID!) {
		getNominationConfiguration(companyId: $companyId, configId: $configId) {
			id
			createdAt
			startDate
			status
			updatedAt
			badgeId
			companyId
			configId
			customFormId
			endDate
			awardedCount
			nominationConfigurationId
			nominatorEmployeeLevelId
			nominatorEmployeePayTypeId
			nominatorEmployeelogInTypeId
			nomineeEmployeeLevelId
			nomineeEmployeePayTypeId
			nomineeEmployeelogInTypeId
			snappyCampaignTierId
			snappyGrantId
		}
	}
`;

const useManageNominatedReward = (props = {}) => {
	const { client, currentUser } = props;
	const [badgeList, setBadgeList] = useState([]);
	const empLoginType = [
		{
			id: 'b87ecb2e-3f7d-4592-b137-6292c2d2f5a9',
			logInType: 'Standard',
		},
		{
			id: 'eaf49841-92d7-4f14-ae02-fc8cb678cf48',
			logInType: 'SSO',
		},
	];
	const empPayType = [
		{
			id: 'c261e9b8-e3fe-4a88-af11-f3c4237c3e9b',
			payType: 'Contract',
		},
		{
			id: 'da9c2f8d-4a6a-4897-b615-e7b1f1c543d2',
			payType: 'Salary',
		},
	];
	const empLevel = [
		{
			id: 1,
			level: 'Level 1',
		},
		{
			id: 2,
			level: 'Level 2',
		},
		{
			id: 3,
			level: 'Level 3',
		},
		{
			id: 4,
			level: 'Level 4',
		},
		{
			id: 5,
			level: 'Level 5',
		},
		{
			id: 6,
			level: 'Level 6',
		},
		{
			id: 7,
			level: 'Level 7',
		},
		{
			id: 8,
			level: 'Level 8',
		},
		{
			id: 9,
			level: 'Level 9',
		},
	];
	const [showLoader, setShowLoader] = useState({
		badgeList: false,
		empLoginType: false,
		empPayType: false,
		empLevel: false,
	});

	const getAllBadgeList = async () => {
		setShowLoader({
			badgeList: true,
			empLoginType: true,
			empPayType: true,
			empLevel: true,
		});

		try {
			if (!client) {
				console.error('Apollo client not available');
				setShowLoader({
					...showLoader,
					badgeList: false,
				});
				return;
			}

			const companyId = currentUser?.companyId || currentUser?.company?.id;

			if (!companyId) {
				console.warn('Company ID not found in currentUser:', currentUser);
				setBadgeList([]);
				setShowLoader((prev) => ({
					...prev,
					badgeList: false,
				}));
				return;
			}

			const { data, error } = await client.query({
				query: QUERY_BADGES_BY_COMPANY_ID,
				variables: {
					companyId: companyId,
					limit: 100,
					nextToken: null,
				},
				fetchPolicy: 'network-only',
			});

			if (error) {
				console.error(error);
				setShowLoader({
					...showLoader,
					badgeList: false,
				});
				return;
			}

			if (data?.queryBadgesByCompanyIdIndex?.items) {
				const enrichedBadges = data.queryBadgesByCompanyIdIndex.items.map(
					(badge) => {
						const badgeTypeCode =
							REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId] || '';

						let iconInfo = null;
						if (badge.iconId) {
							iconInfo = ICON_COMPLETE_MAP.find(
								(icon) => icon.id === badge.iconId
							);
						}

						return {
							...badge,
							badgeType: badgeTypeCode,
							iconInfo,
						};
					}
				);

				const filteredBadges = enrichedBadges.filter(
					(badge) =>
						badge.badgeType === 'NOMINATED' && badge.visibility !== false
				);
				setBadgeList(filteredBadges);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setShowLoader({
				...showLoader,
				badgeList: false,
			});
		}
	};

	const createUpdateNominationConfiguration = async (values) => {
		const nominationCriteria = values?.nominationCriteria;
		let payload = {
			companyId: currentUser?.companyId,
			customFormId: values.customFormId || null,
			badgeId: values.badgeId,
			startDate: values.startDate,
			endDate: values.endDate,
			awardedCount: parseInt(
				nominationCriteria?.nominator?.awardedCount || '1'
			),
		};
		if (values.id) {
			payload.id = values.id;
		}
		if (nominationCriteria?.nominator?.employeeLevelId?.length > 0) {
			payload.nominatorEmployeeLevelId =
				nominationCriteria.nominator.employeeLevelId;
		}
		if (nominationCriteria?.nominee?.employeeLevelId?.length > 0) {
			payload.nomineeEmployeeLevelId =
				nominationCriteria.nominee.employeeLevelId;
		}
		// On create, set the initial status from the configured dates using the
		// same rules as the backend daily job (Pending -> Open -> Closed). On
		// edit, leave status untouched so the backend job stays the source of
		// truth (and we don't clobber Complete/Submitted/Closed).
		if (!values.id) {
			const now = new Date();
			const start = new Date(payload.startDate);
			const end = new Date(payload.endDate);
			if (now < start) {
				payload.status = 'Pending';
			} else if (now <= end) {
				payload.status = 'Open';
			} else {
				payload.status = 'Closed';
			}
		}
		if (nominationCriteria?.nominee?.employeelogInTypeId) {
			payload.nomineeEmployeelogInTypeId =
				nominationCriteria.nominee.employeelogInTypeId;
		}
		if (nominationCriteria?.nominee?.employeePayTypeId) {
			payload.nomineeEmployeePayTypeId =
				nominationCriteria.nominee.employeePayTypeId;
		}
		if (nominationCriteria?.nominator?.employeelogInTypeId) {
			payload.nominatorEmployeelogInTypeId =
				nominationCriteria.nominator.employeelogInTypeId;
		}
		if (nominationCriteria?.nominator?.employeePayTypeId) {
			payload.nominatorEmployeePayTypeId =
				nominationCriteria.nominator.employeePayTypeId;
		}
		try {
			const { data, error } = await client.mutate({
				mutation: values.id
					? UPDATE_NOMINATION_CONFIG
					: CREATE_NOMINATION_CONFIG,
				variables: {
					input: { ...payload },
				},
			});
			if (error) throw error;
			return values.id
				? data?.updateNominationConfiguration
				: data?.createNominationConfiguration;
		} catch (e) {
			console.log(e);
			return null;
		}
	};

	const getNominationConfigurationById = async (id) => {
		try {
			const { data, error } = await client.query({
				query: GET_NOMINATION_CONFIG,
				variables: {
					companyId: currentUser.companyId,
					configId: id,
				},
				fetchPolicy: 'network-only',
			});
			return data?.getNominationConfiguration;
		} catch (e) {
			console.error(e);
		}
	};

	useEffect(() => {
		getAllBadgeList();
	}, [currentUser?.companyId]);

	return {
		empLevel,
		badgeList,
		empLoginType,
		empPayType,
		loading: Object.values(showLoader).flat().includes(true),
		createUpdateNominationConfiguration,
		getNominationConfigurationById,
	};
};

export default useManageNominatedReward;
